/** @jsxRuntime classic */
/** @jsx jsx */
import * as THREE from "three";
import { useRef, Suspense, useState, useCallback, useMemo, useEffect } from "react";
import { Canvas, useLoader, useThree } from "@react-three/fiber";
import { OrbitControls, RoundedBox, meshStandardMaterial, Plane } from "@react-three/drei";
import { css, jsx } from "@emotion/react";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import URDFLoader from "urdf-loader";


import useStore from './store';

const theme = css`
  height: 85vh;
  background-color: #272727;
`;

const mouse = new THREE.Vector2();
const raycaster = new THREE.Raycaster();

const toMouseCoord = (el, e, v) => {
  v.x = ((e.pageX - el.offsetLeft) / el.offsetWidth) * 2 - 1;
  v.y = -((e.pageY - el.offsetTop) / el.offsetHeight) * 2 + 1;
};

const getCollisions = (camera, robot, mouse) => {
  if (!robot) return [];
  raycaster.setFromCamera(mouse, camera);
  const meshes = [];
  robot.traverse(c => c.type === "Mesh" && meshes.push(c));
  return raycaster.intersectObjects(meshes);
};

const isJoint = j => j.isURDFJoint && j.jointType !== "fixed";

const findNearestJoint = m => {
  let curr = m;
  while (curr) {
    if (isJoint(curr)) break;
    curr = curr.parent;
  }
  return curr;
};

const LoadModel = ({ filepath }) => {
  const [hovered, setHovered] = useState(null);
  const { camera, gl } = useThree();
  const ref = useRef();
  document.robotRef=ref;

  const robot = useLoader(URDFLoader, filepath, loader => {
    // Set working path to resolve relative mesh paths
    loader.workingPath = `${process.env.PUBLIC_URL}/urdf/`;
    loader.fetchOptions = { headers: { Accept: "application/vnd.github.v3.raw" } };
  });


  const onMouseMove = useCallback(
    event => {
      try {
        if (!robot) return;

        toMouseCoord(gl.domElement, event, mouse);
        const collision = getCollisions(camera, robot, mouse).shift() || null;

      } catch (error) {
        console.error("Error during onMouseMove:", error);
      }
    },
    [camera, gl, hovered, robot]
  );

  useEffect(() => {
    if (gl && gl.domElement) {
      gl.domElement.addEventListener('mousemove', onMouseMove);
      return () => {
        gl.domElement.removeEventListener('mousemove', onMouseMove);
      };
    }
  }, [gl, onMouseMove]);

  return (
    <mesh
      position={[0, 0, 0]}
      rotation={[-0.5 * Math.PI, 0, Math.PI]}
      scale={[10, 10, 10]}
    >
      <primitive
        ref={ref}
        object={robot}
        dispose={null}
      />
    </mesh>
  );
};

export const Work = () => {
  // Parse URL parameter
  const [loaded, setLoaded]=useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const modelPath = urlParams.get('filepath') || `${process.env.PUBLIC_URL}/urdf/mairon_robot.urdf`;

  const robotLocation=useStore((state)=>state.robotLocation);
  const robotArmState=useStore((state)=>state.robotArmState);
  const openDrawer=useStore((state)=>state.openDrawer);
  const openMachineDoor=useStore((state)=>state.openMachineDoor);
  
  
  const checkRobotLoader=()=>{
    if(!document.robotRef){
      setTimeout(()=>{checkRobotLoader()},
        200
      )      
      return;
    } 
    if(!document.robotRef.current){
      setTimeout(()=>{
        checkRobotLoader();
      },150);
    } else {
      const robot=document.robotRef.current;

      const ubicacion=robotLocation;
      robot.castShadow=true;
      if (ubicacion=="almacen"){
        robot.position.x=0;
        robot.position.y=0;
        robot.rotation.z=0;
      } else if(ubicacion=="frente_almacen"){
        robot.position.x=-.8;
        robot.position.y=0;
        robot.rotation.z=0;        
      } else if(ubicacion=="frente_maquina"){
        robot.position.x=-2;
        robot.position.y=0;
        robot.rotation.z=-Math.PI/2;        
      } else if(ubicacion=="maquina"){
        robot.position.x=-3.8;
        robot.position.y=-3.5;
        robot.rotation.z=-Math.PI/2;        
      } else if(ubicacion=="metrologia"){
        robot.position.x=-5;
        robot.position.y=0;
        robot.rotation.z=Math.PI;
      } else if(ubicacion=="frente_metrologia"){
        robot.position.x=-2;
        robot.position.y=0;
        robot.rotation.z=Math.PI;
      } else if(ubicacion=="cargador"){
        robot.position.x=-1;
        robot.position.y=2;
        robot.rotation.z=Math.PI/2;
      }

      
      const position=robotArmState;
      //const position="extendido0";

      if(position=="reposo"){
        robot.joints.ur_wrist_1_joint.rotation.z=0.2;
        robot.joints.ur_wrist_2_joint.rotation.z=1.2;
        robot.joints.ur_wrist_3_joint.rotation.z=1.2;
                
        robot.joints.ur_base_joint.rotation.z=-Math.PI/2;// Math.PI/2;
        robot.joints.ur_shoulder_pan_joint.rotation.z=Math.PI/2;
        robot.joints.ur_shoulder_lift_joint.rotation.z=-3.3;
        robot.joints.ur_elbow_joint.rotation.z=Math.PI-0.6;

      } else if(position=="extendido0"){
        robot.joints.ur_wrist_1_joint.rotation.z=0.2;
        robot.joints.ur_wrist_2_joint.rotation.z=1.2;
        robot.joints.ur_wrist_3_joint.rotation.z=1.2;
                
        robot.joints.ur_base_joint.rotation.z=-Math.PI/2;// Math.PI/2;
        robot.joints.ur_shoulder_pan_joint.rotation.z=Math.PI/2;
        robot.joints.ur_shoulder_lift_joint.rotation.z=-3;
        robot.joints.ur_elbow_joint.rotation.z=Math.PI-0.8;
        
      } else if(position=="extendido1"){
        robot.joints.ur_wrist_1_joint.rotation.z=0.2;
        robot.joints.ur_wrist_2_joint.rotation.z=1.2;
        robot.joints.ur_wrist_3_joint.rotation.z=1.2;
                
        robot.joints.ur_base_joint.rotation.z=-Math.PI/2;// Math.PI/2;
        robot.joints.ur_shoulder_pan_joint.rotation.z=Math.PI/2;
        robot.joints.ur_shoulder_lift_joint.rotation.z=-2.5;
        robot.joints.ur_elbow_joint.rotation.z=Math.PI/2+0.5;
      } else if(position=="extendido2"){
        robot.joints.ur_wrist_1_joint.rotation.z=0.2;
        robot.joints.ur_wrist_2_joint.rotation.z=1.2;
        robot.joints.ur_wrist_3_joint.rotation.z=1.2;
                
        robot.joints.ur_base_joint.rotation.z=-Math.PI/2;// Math.PI/2;
        robot.joints.ur_shoulder_pan_joint.rotation.z=Math.PI/2;
        robot.joints.ur_shoulder_lift_joint.rotation.z=-1.5;
        robot.joints.ur_elbow_joint.rotation.z=Math.PI/2;                  
      } else if(position=="extendido3"){
        robot.joints.ur_wrist_1_joint.rotation.z=0.2;
        robot.joints.ur_wrist_2_joint.rotation.z=1.2;
        robot.joints.ur_wrist_3_joint.rotation.z=1.2;
                
        robot.joints.ur_base_joint.rotation.z=-Math.PI/2;// Math.PI/2;
        robot.joints.ur_shoulder_pan_joint.rotation.z=Math.PI/2;
        robot.joints.ur_shoulder_lift_joint.rotation.z=-Math.PI/3;
        robot.joints.ur_elbow_joint.rotation.z=Math.PI/2;          
      } else if(position=="extendido4"){
        robot.joints.ur_wrist_1_joint.rotation.z=0.2;
        robot.joints.ur_wrist_2_joint.rotation.z=1.2;
        robot.joints.ur_wrist_3_joint.rotation.z=1.2;
                
        robot.joints.ur_base_joint.rotation.z=-Math.PI/2;// Math.PI/2;
        robot.joints.ur_shoulder_pan_joint.rotation.z=Math.PI/2;
        robot.joints.ur_shoulder_lift_joint.rotation.z=-0.5;
        robot.joints.ur_elbow_joint.rotation.z=Math.PI/2;          
      } else if(position=="extendido5"){
        robot.joints.ur_wrist_1_joint.rotation.z=0.2;
        robot.joints.ur_wrist_2_joint.rotation.z=1.2;
        robot.joints.ur_wrist_3_joint.rotation.z=1.2;
                
        robot.joints.ur_base_joint.rotation.z=-Math.PI/2;// Math.PI/2;
        robot.joints.ur_shoulder_pan_joint.rotation.z=Math.PI/2;
        robot.joints.ur_shoulder_lift_joint.rotation.z=-0.5;
        robot.joints.ur_elbow_joint.rotation.z=Math.PI/2;          
      }


      
    }
  }
  checkRobotLoader();

  return (
    <div css={theme}>
      <Canvas camera={{ position: [95, 40, 40],  }}>

        <Plane args={[200, 200]} rotation={[-0.5 * Math.PI, 0, Math.PI]}>
            <meshStandardMaterial color="#a2a2a2"  />
        </Plane>

        <hemisphereLight
          skyColor={"#455A64"}
          groundColor={"#000"}
          intensity={1.8}
          position={[4, 8, 4]}
          castShadow
        />

        <directionalLight
          color={0xffffff}
          position={[20, 10, 10]}
          intensity={1.8}
          shadow-mapWidth={500}
          shadow-mapHeight={50}
          castShadow
        />
        <Suspense fallback={null}>
          <LoadModel filepath={modelPath} />
        </Suspense>

        <RoundedBox // Almacen
          args={[10, 6, 16]} // Width, height, depth. Default is [1, 1, 1]
          radius={0.05} // Radius of the rounded corners. Default is 0.05
          steps={1} // Extrusion steps. Default is 1
          smoothness={4} // The number of curve segments. Default is 4
          position={[-16 - 5, 3, 0]}
          bevelSegments={4} // The number of bevel segments. Default is 4, setting it to 0 removes the bevel, as a result the texture is applied to the whole geometry.
          creaseAngle={0.4} // Smooth normals everywhere except faces that meet at an angle greater than the crease angle
        >
          <meshStandardMaterial color="#f3f3f3"  />
        </RoundedBox>        

        { openDrawer==true &&
            <RoundedBox // Cajón almacén
            args={[10, 2, 16]} // Width, height, depth. Default is [1, 1, 1]          
            radius={0.05} // Radius of the rounded corners. Default is 0.05
            steps={1} // Extrusion steps. Default is 1
            smoothness={4} // The number of curve segments. Default is 4
            position={[-13 - 5, 3, 0]}
            bevelSegments={4} // The number of bevel segments. Default is 4, setting it to 0 removes the bevel, as a result the texture is applied to the whole geometry.
            creaseAngle={0.4} // Smooth normals everywhere except faces that meet at an angle greater than the crease angle
          >
            <meshStandardMaterial color="#f3f3f3"  />
          </RoundedBox>                 
        }
 

        <RoundedBox // Metrologia
          args={[10, 6, 16]} // Width, height, depth. Default is [1, 1, 1]
          radius={0.05} // Radius of the rounded corners. Default is 0.05
          steps={1} // Extrusion steps. Default is 1
          smoothness={4} // The number of curve segments. Default is 4
          position={[80 - 5, 3, 0]}
          bevelSegments={4} // The number of bevel segments. Default is 4, setting it to 0 removes the bevel, as a result the texture is applied to the whole geometry.
          creaseAngle={0.4} // Smooth normals everywhere except faces that meet at an angle greater than the crease angle
        >
          <meshStandardMaterial color="#f30505" />
        </RoundedBox>   

        <RoundedBox // Máquina
          args={[50, 38, 14]} // Width, height, depth. Default is [1, 1, 1]
          radius={0.05} // Radius of the rounded corners. Default is 0.05
          steps={1} // Extrusion steps. Default is 1
          smoothness={4} // The number of curve segments. Default is 4
          position={[40 - 5, 7, -50]}
          bevelSegments={4} // The number of bevel segments. Default is 4, setting it to 0 removes the bevel, as a result the texture is applied to the whole geometry.
          creaseAngle={0.4} // Smooth normals everywhere except faces that meet at an angle greater than the crease angle
        >
          <meshStandardMaterial color="#05e5e5"  />
        </RoundedBox>        

        { openMachineDoor==true &&
            <RoundedBox // Puerta Máquina
            args={[20, 31, 14]} // Width, height, depth. Default is [1, 1, 1]
            radius={0.05} // Radius of the rounded corners. Default is 0.05
            steps={1} // Extrusion steps. Default is 1
            smoothness={4} // The number of curve segments. Default is 4
            position={[40 - 5, 7, -49.9]}
            bevelSegments={4} // The number of bevel segments. Default is 4, setting it to 0 removes the bevel, as a result the texture is applied to the whole geometry.
            creaseAngle={0.4} // Smooth normals everywhere except faces that meet at an angle greater than the crease angle
          >
            <meshPhongMaterial color="#0a0a0a"  />
          </RoundedBox>                     
        }


        <OrbitControls />
        {/* <gridHelper args={[10, 10]} /> */}
        <axesHelper />
      </Canvas>
    </div>
  );
};
